import { useState } from 'react';

import { useAuth } from '@/auth/useAuth';
import { useLang } from '@/i18n/useLang';

export function LoginPage() {
  const { login, isLoggingIn, loginError } = useAuth();
  const { lang, setLang } = useLang();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void login(username.trim(), password);
  };

  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md rounded-3xl bg-stone-900/80 p-8 ring-1 ring-stone-800 backdrop-blur">
        <header className="mb-6 flex items-center justify-between">
          <div>
            <span className="rounded-md bg-saffron-700/40 px-2 py-1 text-xs font-bold uppercase tracking-wider text-saffron-100">
              {t('ज्योतिष', 'Astro')}
            </span>
            <h1 className="mt-3 text-2xl font-bold text-stone-100">
              {t('प्रवेश करें', 'Sign in')}
            </h1>
            <p className="mt-1 text-sm text-stone-400">
              {t('अधिकृत उपयोगकर्ता', 'Authorised users only')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setLang(lang === 'hi' ? 'en' : 'hi')}
            className="rounded-md border border-saffron-600 bg-stone-950 px-3 py-1 text-sm text-saffron-200 hover:bg-saffron-700/30"
            aria-label={lang === 'hi' ? 'Switch to English' : 'हिन्दी'}
          >
            {lang === 'hi' ? 'EN' : 'हिं'}
          </button>
        </header>

        <form onSubmit={onSubmit} className="space-y-4" autoComplete="on">
          <Field
            id="username"
            label={t('उपयोगकर्ता नाम', 'Username')}
            value={username}
            onChange={setUsername}
            autoComplete="username"
            required
          />
          <Field
            id="password"
            label={t('पासवर्ड', 'Password')}
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
          />

          {loginError && (
            <div role="alert" className="rounded-md bg-rose-950/60 px-3 py-2 text-sm text-rose-200 ring-1 ring-rose-900">
              {loginError}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoggingIn || !username || !password}
            className="w-full rounded-md bg-saffron-600 px-4 py-2 font-semibold text-stone-950 transition hover:bg-saffron-500 disabled:cursor-not-allowed disabled:bg-stone-700 disabled:text-stone-500"
          >
            {isLoggingIn
              ? t('प्रवेश हो रहा है…', 'Signing in…')
              : t('प्रवेश करें', 'Sign in')}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-stone-500">
          {t(
            'समस्या हो रही है? प्रोफ़ेसर से संपर्क करें।',
            'Trouble signing in? Contact the professor.',
          )}
        </p>
      </div>
    </div>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
  required?: boolean;
}

function Field({ id, label, value, onChange, type = 'text', autoComplete, required }: FieldProps) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium uppercase tracking-wider text-saffron-400">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        autoComplete={autoComplete}
        className="w-full rounded-md border border-stone-700 bg-stone-950 px-3 py-2 text-stone-100 outline-none transition focus:border-saffron-500 focus:ring-1 focus:ring-saffron-500"
      />
    </div>
  );
}
